/**
 * PatternMatcher - Detector de patrones clínicos
 * Identifica triadas, síndromes y patrones de urgencia
 */

import { 
  ClinicalPattern, 
  PatternCriterion, 
  ClinicalNodeData,
  DetectedPattern,
  TriagePriority 
} from '../types/medical';
import { CLINICAL_PATTERNS } from '../types/clinical';

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export class PatternMatcher {
  patterns: Map<string, ClinicalPattern> = new Map();
  detectedPatterns: Map<string, DetectedPattern> = new Map();

  constructor() {
    this.loadDefaultPatterns();
  }

  private loadDefaultPatterns(): void {
    Object.values(CLINICAL_PATTERNS).forEach(pattern => {
      this.patterns.set(pattern.id, pattern);
    });
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // REGISTRO DE PATRONES
  // ═════════════════════════════════════════════════════════════════════════════
  
  registerPattern(pattern: ClinicalPattern): void {
    this.patterns.set(pattern.id, pattern);
  }

  unregisterPattern(patternId: string): void {
    this.patterns.delete(patternId);
    this.detectedPatterns.delete(patternId);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // DETECCIÓN
  // ═════════════════════════════════════════════════════════════════════════════
  
  evaluate(nodes: ClinicalNodeData[]): DetectedPattern[] {
    const newDetections: DetectedPattern[] = [];

    // Paso 1: patrones genéricos por criterios declarativos
    this.patterns.forEach(pattern => {
      // Sepsis y STEMI tienen reglas especializadas (abajo); saltar aquí.
      if (pattern.id === CLINICAL_PATTERNS.SEPSIS_CRITERIA.id) return;
      if (pattern.id === CLINICAL_PATTERNS.STEMI_PATTERN.id) return;

      const result = this.evaluatePattern(pattern, nodes);
      this.registerDetection(result, newDetections);
    });

    // Paso 2: reglas especializadas (umbral clínico vs match estructural)
    const sepsis = this.detectSepsis(nodes);
    if (sepsis) this.registerDetection(sepsis, newDetections);
    const stemi = this.detectSTEMI(nodes);
    if (stemi) this.registerDetection(stemi, newDetections);

    return newDetections;
  }

  private registerDetection(result: DetectedPattern, accumulator: DetectedPattern[]): void {
    if (result.confidence < result.pattern.confidenceThreshold) return;

    const existing = this.detectedPatterns.get(result.pattern.id);
    if (!existing) {
      this.detectedPatterns.set(result.pattern.id, result);
      accumulator.push(result);
    } else if (result.confidence > existing.confidence) {
      this.detectedPatterns.set(result.pattern.id, result);
    }
  }

  private evaluatePattern(pattern: ClinicalPattern, nodes: ClinicalNodeData[]): DetectedPattern {
    const matchedNodes: string[] = [];
    const totalCriteria = pattern.nodeCriteria.length;
    let metCriteria = 0;

    pattern.nodeCriteria.forEach(criterion => {
      const matchingNode = this.findMatchingNode(criterion, nodes);
      
      if (matchingNode) {
        matchedNodes.push(matchingNode.id);
        if (criterion.required) {
          metCriteria++;
        }
      }
    });

    // Calcular confianza
    const requiredCriteria = pattern.nodeCriteria.filter(c => c.required).length;
    let confidence = 0;
    
    if (requiredCriteria > 0) {
      confidence = metCriteria / requiredCriteria;
    } else {
      confidence = matchedNodes.length / totalCriteria;
    }

    // Bonus por coincidencias adicionales
    const bonus = (matchedNodes.length - requiredCriteria) * 0.1;
    confidence = Math.min(1, confidence + bonus);

    return {
      pattern,
      matchedNodes,
      confidence,
      detectedAt: new Date().toISOString(),
    };
  }

  private findMatchingNode(criterion: PatternCriterion, nodes: ClinicalNodeData[]): ClinicalNodeData | null {
    for (const node of nodes) {
      // Verificar tipo
      if (criterion.nodeType && node.type !== criterion.nodeType) {
        continue;
      }

      // Verificar códigos de concepto
      if (criterion.conceptCodes && criterion.conceptCodes.length > 0) {
        const codeMatches = criterion.conceptCodes.some(code => 
          node.concept.code === code ||
          node.concept.code.includes(code)
        );
        if (!codeMatches) continue;
      }

      // Verificar severidad
      if (criterion.severity && criterion.severity.length > 0) {
        if (!criterion.severity.includes(node.severity || '')) {
          continue;
        }
      }

      // Verificar predicado de valor
      if (criterion.valuePredicate) {
        const value = node.valueQuantity?.value ?? node.valueString ?? node.valueBoolean;
        if (!criterion.valuePredicate(value)) {
          continue;
        }
      }

      return node;
    }

    return null;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // REGLAS ESPECIALIZADAS (umbrales clínicos, no solo match estructural)
  // Se invocan desde evaluate(); no exponen API pública separada para mantener
  // una sola vía de detección.
  // ═════════════════════════════════════════════════════════════════════════════

  private detectSepsis(nodes: ClinicalNodeData[]): DetectedPattern | null {
    // Verificar criterios de Sepsis-3
    const suspectedInfection = nodes.some(n => 
      n.type === 'symptom' && 
      (n.concept.code === '386661006' || n.concept.display?.toLowerCase().includes('infección'))
    );

    const alteredMentalStatus = nodes.some(n =>
      n.concept.code === '40917007' || n.concept.code === '407389004'
    );

    const hypotension = nodes.some(n =>
      n.type === 'vital_sign' &&
      n.concept.code === '8480-6' &&
      n.valueQuantity &&
      n.valueQuantity.value <= 100
    );

    const qSOFA = (alteredMentalStatus ? 1 : 0) + (hypotension ? 1 : 0);

    if (suspectedInfection && qSOFA >= 2) {
      return {
        pattern: CLINICAL_PATTERNS.SEPSIS_CRITERIA,
        matchedNodes: nodes
          .filter(n => 
            n.concept.code === '386661006' ||
            n.concept.code === '40917007' ||
            n.concept.code === '8480-6'
          )
          .map(n => n.id),
        confidence: 0.9,
        detectedAt: new Date().toISOString(),
      };
    }

    return null;
  }

  private detectSTEMI(nodes: ClinicalNodeData[]): DetectedPattern | null {
    const chestPain = nodes.find(n =>
      n.type === 'symptom' &&
      (n.concept.code === '279035001' || n.concept.display?.toLowerCase().includes('pecho'))
    );

    const elevatedTroponin = nodes.find(n =>
      n.type === 'laboratory' &&
      (n.concept.code === '6598-7' || n.concept.code === '10839-9') &&
      n.valueQuantity &&
      n.valueQuantity.value > (n.referenceRange?.high || 14)
    );

    const stElevation = nodes.find(n =>
      n.type === 'imaging' &&
      n.concept.display?.toLowerCase().includes('st')
    );

    if (chestPain && elevatedTroponin) {
      // Dolor torácico severo + troponina fuera de rango ya es señal suficiente
      // para levantar la sospecha clínica (NSTEMI al menos). La elevación del
      // ST en imaging sube la certeza a STEMI franco.
      const confidence = stElevation ? 1.0 : 0.92;
      return {
        pattern: CLINICAL_PATTERNS.STEMI_PATTERN,
        matchedNodes: [chestPain.id, elevatedTroponin.id, stElevation?.id].filter(Boolean) as string[],
        confidence,
        detectedAt: new Date().toISOString(),
      };
    }

    return null;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // UTILIDADES
  // ═════════════════════════════════════════════════════════════════════════════
  
  getDetectedPatterns(): DetectedPattern[] {
    return Array.from(this.detectedPatterns.values());
  }

  hasDetectedPattern(patternId: string): boolean {
    return this.detectedPatterns.has(patternId);
  }

  getMostUrgentPattern(): DetectedPattern | null {
    const priorities: Record<TriagePriority, number> = {
      immediate: 0,
      delayed: 1,
      minor: 2,
      expectant: 3,
      deceased: 4,
    };

    return Array.from(this.detectedPatterns.values())
      .sort((a, b) => priorities[a.pattern.urgency] - priorities[b.pattern.urgency])[0] || null;
  }

  clearDetections(): void {
    this.detectedPatterns.clear();
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // SCORING DE URGENCIA
  // ═════════════════════════════════════════════════════════════════════════════
  
  calculateUrgencyScore(nodes: ClinicalNodeData[]): number {
    let score = 0;

    // Factores de riesgo
    const criticalNodes = nodes.filter(n => n.severity === 'life-threatening');
    score += criticalNodes.length * 3;

    const severeNodes = nodes.filter(n => n.severity === 'severe');
    score += severeNodes.length * 2;

    // Signos vitales anormales
    const abnormalVitals = nodes.filter(n => {
      if (!n.valueQuantity || !n.referenceRange) return false;
      const val = n.valueQuantity.value;
      return (n.referenceRange.low !== undefined && val < n.referenceRange.low) ||
             (n.referenceRange.high !== undefined && val > n.referenceRange.high);
    });
    score += abnormalVitals.length * 2;

    // Patrones detectados
    const patterns = this.evaluate(nodes);
    patterns.forEach(p => {
      if (p.pattern.urgency === 'immediate') score += 5;
      else if (p.pattern.urgency === 'delayed') score += 2;
    });

    return Math.min(100, score);
  }

  recommendTriagePriority(nodes: ClinicalNodeData[]): TriagePriority {
    const score = this.calculateUrgencyScore(nodes);
    const patterns = this.evaluate(nodes);

    // Verificar patrones críticos
    const hasCriticalPattern = patterns.some(p => 
      p.pattern.urgency === 'immediate' && p.confidence > 0.8
    );

    if (hasCriticalPattern) return 'immediate';
    if (score >= 10) return 'immediate';
    if (score >= 6) return 'delayed';
    if (score >= 3) return 'minor';
    return 'minor';
  }
}

export default PatternMatcher;
